import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import { buttonVariants } from '@/utils/button-variants'
import { Slot } from '@radix-ui/react-slot'
import type { VariantProps } from 'class-variance-authority'
import { Download, LoaderCircle, X } from 'lucide-react'
import * as React from 'react'

function Button({
  className,
  variant,
  size,
  tooltipContent,
  tooltipContentClassName,
  asChild = false,
  ...props
}: React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
    tooltipContent?: string
    tooltipContentClassName?: string
  }) {
  const Comp = asChild ? Slot : 'button'

  if (tooltipContent)
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <Comp
            data-slot='button'
            className={cn(buttonVariants({ variant, size, className }), 'capitalize')}
            {...props}
          />
        </TooltipTrigger>
        <TooltipContent className={cn(tooltipContentClassName)}>
          <p>{tooltipContent}</p>
        </TooltipContent>
      </Tooltip>
    )

  return (
    <Comp data-slot='button' className={cn(buttonVariants({ variant, size, className }), 'capitalize')} {...props} />
  )
}

export { Button }

export const ClearFiltersButton = ({ onClick, className }: { onClick: () => void; className?: string }) => {
  return (
    <div className='flex justify-end border-t pt-3'>
      <Button
        variant='secondary'
        size='sm'
        onClick={onClick}
        className={cn(
          'bg-destructive/5 border border-destructive/50 text-destructive hover:bg-destructive/10 text-xs [&_svg:not([class*="size-"])]:size-[13px]',
          className
        )}
      >
        <X className='size-3' />
        Clear Filters
      </Button>
    </div>
  )
}

export const TemplateDownloadButton = ({ onClick, loading }: { onClick: () => void; loading?: boolean }) => {
  return (
    <Button variant='outline' onClick={onClick} disabled={loading}>
      {loading ? <LoaderCircle className='h-4 w-4 animate-spin' /> : <Download className='h-4 w-4' />}
      {loading ? 'Downloading Template...' : 'Download Sample Template'}
    </Button>
  )
}
